<script setup lang="ts">
import { ref, watch } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { $t, usePrevioCore } from '@previo/core'
import { type IErrorState } from './model/ErrorState'
import Segment from './components/Segment.vue'

type Emits = {
  (e: 'next'): void
  (e: 'prev'): void
}

const props = defineProps({
  defaultTime: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) return true
      return dayjs(value, 'HH:mm', true).isValid()
    },
    required: false,
  },
  minuteIncrement: {
    type: Number,
    default: 1,
  },
})

const model = defineModel<Dayjs | null>()
const emits = defineEmits<Emits>()

const { UUID } = usePrevioCore()

const id = UUID.v7()

const segmentComponent = ref<InstanceType<typeof Segment>>()
const text = ref<string>('mm')
const buffer = ref<string>('')

const isValid = (): boolean => {
  if (!model.value) {
    return false
  }

  return model.value.tz().isValid() && text.value !== 'mm'
}

const validate = (errorState: IErrorState): void => {
  if (isValid()) {
    return
  }

  errorState.set($t('The value {value} is required', { value: 'mm' }))
}

const isValidMinute = (minute: number): boolean => {
  return minute >= 0 && minute <= 59
}

const focus = (): void => {
  segmentComponent.value?.focus()
}

const ensureValid = (): void => {
  const raw = buffer.value.trim()

  if (!/^\d+$/.test(raw)) {
    return
  }

  const minute = parseInt(raw, 10)

  if (minute < 0 || minute > 59) {
    return
  }

  if (!model.value) {
    return
  }

  model.value = model.value.tz().minute(minute)

  emits('next')
}

const onInput = (value: string): void => {
  buffer.value = (buffer.value + value).slice(-2)
  const minute = parseInt(buffer.value, 10)

  if (buffer.value.length === 2) {
    const base =
      model.value ?? (props.defaultTime ? dayjs(props.defaultTime, 'HH:mm') : dayjs().tz())
    const safeMinute = isValidMinute(minute) ? minute : Math.min(Math.max(minute, 0), 59)

    model.value = base.set('minute', safeMinute)

    emits('next')
    buffer.value = ''
  } else {
    text.value = buffer.value
  }
}

const onDelete = (): void => {
  buffer.value = ''
  text.value = 'mm'
}

const onAdd = (): void => {
  model.value = model.value?.tz().add(props.minuteIncrement, 'minute')
}

const onSubtract = (): void => {
  model.value = model.value?.tz().subtract(props.minuteIncrement, 'minute')
}

const onNext = (): void => {
  ensureValid()
  emits('next')
}

const onPrev = (): void => {
  ensureValid()
  emits('prev')
}

const onConfirm = (): void => {
  ensureValid()
}

watch(
  model,
  () => {
    if (!model.value) {
      text.value = 'mm'
      return
    }

    text.value = model.value.tz().format('mm')
  },
  { immediate: true },
)

defineExpose({
  focus,
  validate,
})
</script>

<template>
  <segment
    :id="id"
    ref="segmentComponent"
    :text="text"
    @input="onInput"
    @delete="onDelete"
    @next="onNext"
    @prev="onPrev"
    @add="onAdd"
    @subtract="onSubtract"
    @confirm="onConfirm"
  />
</template>
